import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { Globe, LayoutDashboard, ListIcon, Plus, Search, RefreshCw, Database, X } from 'lucide-react';
import { MobileSubMenuHeader } from '@/components/layout';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { useWorkspace } from '@/context/WorkspaceContext';
import { generateHMACSignature } from '@/components/Sites/utils';
import { SiteCard } from '@/components/Sites/SiteCard';
import { SiteListRow } from '@/components/Sites/SiteListRow';
import { ConnectSiteModal } from '@/components/Sites/ConnectSiteModal';
import type { Site, WpUser } from '@/components/Sites/types';

interface SitesProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

export default function Sites({ isGlobalDarkMode, isMobile = false }: SitesProps) {
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();

  const [activeTab, setActiveTab] = useState('my-websites');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectedSites, setConnectedSites] = useState<Site[]>([]);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const [wpUsers, setWpUsers] = useState<Record<string, WpUser[]>>({});
  const [openUserDropdown, setOpenUserDropdown] = useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<string | null>(null);

  const [loginLoading, setLoginLoading] = useState<string | null>(null);
  const [refreshingThumbnail, setRefreshingThumbnail] = useState<string | null>(null);

  useEffect(() => {
    const closeDropdowns = () => { setActiveDropdown(null); setOpenUserDropdown(null); };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  const loadSites = async () => {
    if (!currentWorkspace?.id) return;
    try {
      const { data, error } = await supabase
        .from('workspace_sites')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching sites:', error);
      } else if (data) {
        const mappedSites: Site[] = data.map(dbSite => ({
          id: dbSite.id,
          name: dbSite.name,
          url: dbSite.url,
          status: dbSite.status as any,
          uptimeStatus: dbSite.uptime_status || 'unknown',
          uptime_status: dbSite.uptime_status || 'unknown',
          uptime_enabled: dbSite.uptime_enabled,
          uptime_last_check: dbSite.uptime_last_check,
          uptime_last_up_at: dbSite.uptime_last_up_at,
          uptime_last_down_at: dbSite.uptime_last_down_at,
          uptime_interval: dbSite.uptime_interval,
          uptimeLastDownAt: dbSite.uptime_last_down_at,
          lastPing: dbSite.last_ping,
          wpVersion: dbSite.wp_version || 'Unknown',
          publicKey: dbSite.public_key,
          secretKey: dbSite.secret_key,
          workspace_id: dbSite.workspace_id,
          created_at: dbSite.created_at,
          thumbnail_url: dbSite.thumbnail_url,
          defaultWpUserId: dbSite.default_wp_user_id ?? null,
          defaultWpUserName: dbSite.default_wp_user_name ?? null,
          backupEnabled: dbSite.backup_enabled,
          backupLastRun: dbSite.backup_last_run,
          backupRecurrence: dbSite.backup_recurrence,
          backupRetention: dbSite.backup_retention,
          backupNextRun: dbSite.backup_next_run,
          backupNextFullRun: dbSite.backup_next_full_run,
          backup_status: dbSite.backup_status,
          backup_progress: dbSite.backup_progress,
          backupTime: dbSite.backup_time,
          backupDay: dbSite.backup_day,
          backupType: dbSite.backup_type,
          incrementalFullRecurrence: dbSite.incremental_full_recurrence,
          incrementalRecurrence: dbSite.incremental_recurrence,
          lastFullBackupKey: dbSite.last_full_backup_key,
          last_plugins_data: dbSite.last_plugins_data,
          last_themes_data: dbSite.last_themes_data,
          last_core_info: dbSite.last_core_info,
          last_perf_mobile: dbSite.last_perf_mobile,
          last_perf_desktop: dbSite.last_perf_desktop,
        }));
        setConnectedSites(mappedSites);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { loadSites(); }, [currentWorkspace?.id]);

  useEffect(() => {
    if (location.pathname === '/sites') setActiveTab('my-websites');
    else if (location.pathname === '/sites/updates') setActiveTab('updates');
    else if (location.pathname === '/sites/backups') setActiveTab('backups');
    else setActiveTab('my-websites');
  }, [location]);

  const openConnectModal = (existingSite?: Site) => {
    setEditingSite(existingSite || null);
    setShowConnectModal(true);
  };

  const fetchWpUsers = async (site: Site) => {
    if (wpUsers[site.id] || loadingUsers === site.id) return;
    try {
      setLoadingUsers(site.id);
      const endpoint = `${site.url}/wp-json/agencyplus/v1/users`;
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const canonicalString = `${timestamp}\nGET\n/agencyplus/v1/users\n`;
      const signature = await generateHMACSignature(site.secretKey, canonicalString);
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'X-AgencyPlus-Public-Key': site.publicKey,
          'X-AgencyPlus-Signature': signature,
          'X-AgencyPlus-Timestamp': timestamp,
        },
      });
      const data = await response.json().catch(() => null);
      if (response.ok && data?.users) {
        setWpUsers(prev => ({ ...prev, [site.id]: data.users.filter((u: WpUser) => u.can_magic_login) }));
      }
    } catch (e) {
      console.error('Failed to fetch WP users:', e);
    } finally {
      setLoadingUsers(null);
    }
  };

  const handleSetDefaultUser = async (site: Site, user: WpUser) => {
    await supabase
      .from('workspace_sites')
      .update({ default_wp_user_id: user.id, default_wp_user_name: user.display_name })
      .eq('id', site.id);
    setConnectedSites(prev =>
      prev.map(s => s.id === site.id ? { ...s, defaultWpUserId: user.id, defaultWpUserName: user.display_name } : s)
    );
  };

  const handleMagicLogin = async (site: Site, userId?: number) => {
    try {
      setLoginLoading(site.id);
      const endpoint = `${site.url}/wp-json/agencyplus/v1/magic-login`;
      const timestamp = Math.floor(Date.now() / 1000).toString();
      const resolvedUserId = userId ?? site.defaultWpUserId ?? undefined;
      const body = resolvedUserId ? JSON.stringify({ user_id: resolvedUserId }) : JSON.stringify({});
      const canonicalString = `${timestamp}\nPOST\n/agencyplus/v1/magic-login\n${body}`;
      const signature = await generateHMACSignature(site.secretKey, canonicalString);
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-AgencyPlus-Public-Key': site.publicKey,
          'X-AgencyPlus-Signature': signature,
          'X-AgencyPlus-Timestamp': timestamp,
        },
        body,
      });
      const data = await response.json().catch(() => null);
      if (response.ok && data?.success && data?.login_url) {
        window.open(data.login_url, '_blank');
      } else {
        alert(data?.message || 'Failed to generate magic login link.');
      }
    } catch (err: any) {
      alert(err.message || 'Network error during magic login.');
    } finally {
      setLoginLoading(null);
    }
  };

  const handleRefreshThumbnail = async (site: Site) => {
    const isLocalUrl = site.url.includes('.local') || site.url.includes('localhost') || site.url.includes('127.0.0.1');
    if (isLocalUrl) { alert('Thumbnails cannot be generated for locally hosted environments.'); return; }

    try {
      setRefreshingThumbnail(site.id);
      const screenshotUrl = `https://s0.wp.com/mshots/v1/${encodeURIComponent(site.url)}?v=${Date.now()}&w=1200`;
      const imgRes = await fetch(screenshotUrl);
      if (!imgRes.ok) throw new Error('Failed to capture fresh screenshot from provider.');
      const blob = await imgRes.blob();

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('You must be logged in to upload to storage.');

      let deleteKey: string | undefined;
      if (site.thumbnail_url) {
        try { deleteKey = new URL(site.thumbnail_url).pathname.replace(/^\//, ''); } catch { }
      }

      const fileName = `screenshot_${site.id}.jpg`;
      const r2Res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/r2-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ fileName, contentType: 'image/jpeg', folder: `sites/thumbnails/${currentWorkspace?.id}`, deleteKey }),
      });

      const r2Data = await r2Res.json();
      if (!r2Res.ok || !r2Data.url) throw new Error('Failed to request storage upload location.');

      const uploadRes = await fetch(r2Data.url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blob });

      if (uploadRes.ok && r2Data.publicUrl) {
        await supabase.from('workspace_sites').update({ thumbnail_url: r2Data.publicUrl }).eq('id', site.id);
        setConnectedSites(prev => prev.map(s => s.id === site.id ? { ...s, thumbnail_url: r2Data.publicUrl } : s));
      } else {
        throw new Error('Failed to upload the image to R2.');
      }
    } catch (e: any) {
      console.error('Refresh thumbnail failed:', e);
      alert(e.message || 'Failed to refresh thumbnail.');
    } finally {
      setRefreshingThumbnail(null);
    }
  };

  const handleDisconnectSite = async (siteId: string) => {
    if (!confirm('Are you sure you want to disconnect this site? You can easily reconnect it later without losing data.')) return;
    try {
      const { error } = await supabase.from('workspace_sites').update({ status: 'error', public_key: '', secret_key: '' }).eq('id', siteId);
      if (error) throw error;
      setConnectedSites(prev => prev.map(s => s.id === siteId ? { ...s, status: 'error', publicKey: '', secretKey: '' } : s));
    } catch (err: any) {
      alert('Failed to disconnect site: ' + err.message);
    }
  };

  const handleDeleteSite = async (siteId: string) => {
    if (!confirm('Are you sure you want to remove this site?')) return;
    try {
      const { error } = await supabase.from('workspace_sites').delete().eq('id', siteId);
      if (error) throw error;
      setConnectedSites(prev => prev.filter(s => s.id !== siteId));
    } catch (err: any) {
      alert('Failed to remove site: ' + err.message);
    }
  };

  const filteredSites = connectedSites.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));

  const renderTabContent = () => {
    switch (activeTab) {
      case 'my-websites':
        return (
          <div className="w-full px-4 sm:px-6 md:px-10 py-4 sm:py-6">
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
              <div className="flex items-center space-x-4">
                <h1 className="text-3xl font-bold text-foreground">My Websites</h1>
              </div>

              <div className={`flex flex-wrap items-center gap-4 ${isMobile ? 'hidden' : ''}`}>
                <div className="relative flex items-center h-10">
                  <AnimatePresence initial={false}>
                    {searchOpen ? (
                      <motion.div key="search-open" className="relative flex items-center h-10" initial={{ width: 40, opacity: 0 }} animate={{ width: 256, opacity: 1 }} exit={{ width: 40, opacity: 0 }}>
                        <Search className="absolute left-3 w-4 h-4 text-muted-foreground" />
                        <input autoFocus type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search sites..." onBlur={() => !searchQuery && setSearchOpen(false)} className="w-full h-full pl-9 pr-8 text-sm rounded-lg border bg-white dark:bg-card border-border text-foreground placeholder:text-muted-foreground focus:outline-none" />
                        <button onMouseDown={(e) => { e.preventDefault(); setSearchQuery(''); setSearchOpen(false); }} className="absolute right-3 text-muted-foreground hover:text-foreground">
                          <X className="w-4 h-4" />
                        </button>
                      </motion.div>
                    ) : (
                      <button onClick={() => setSearchOpen(true)} className="flex items-center justify-center w-10 h-10 rounded-lg border bg-white dark:bg-card border-border text-muted-foreground hover:text-foreground transition-colors">
                        <Search className="w-4 h-4" />
                      </button>
                    )}
                  </AnimatePresence>
                </div>

                <div className="relative flex items-center rounded-lg border border-border p-1 bg-white dark:bg-card h-10">
                  <motion.div className="absolute top-1 bottom-1 rounded-sm bg-background shadow-sm" animate={{ left: viewMode === 'grid' ? '4px' : 'calc(50%)', width: 'calc(50% - 4px)' }} transition={{ type: 'spring', stiffness: 400, damping: 35 }} />
                  <button onClick={() => setViewMode('grid')} className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'grid' ? 'text-foreground' : 'text-muted-foreground'}`}>
                    <LayoutDashboard className="w-4 h-4" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`relative z-10 px-3 h-full rounded-sm transition-colors ${viewMode === 'list' ? 'text-foreground' : 'text-muted-foreground'}`}>
                    <ListIcon className="w-4 h-4" />
                  </button>
                </div>

                <button onClick={() => openConnectModal()} className="flex items-center space-x-2 px-6 h-10 bg-workspace-accent text-white rounded-md transition-colors hover:bg-workspace-accent-hover whitespace-nowrap">
                  <Plus className="w-5 h-5" /><span>New Site</span>
                </button>
              </div>
            </div>

            {connectedSites.length === 0 ? (
              <div className="text-center py-20 bg-white dark:bg-card rounded-xl border border-border mt-8">
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-workspace-accent/10">
                  <Globe className="w-10 h-10 text-workspace-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">No Sites Connected</h3>
                <p className="max-w-md mx-auto mb-6 text-muted-foreground">
                  Install the <a href="https://picvniwkpqtbykwgxvtl.supabase.co/functions/v1/agencyplus-connector-updates?action=download" download className="text-foreground font-bold hover:text-workspace-accent transition-colors underline decoration-workspace-accent/30 underline-offset-4">AgencyPlus Connector</a> plugin on your WordPress site, then enter these credentials to securely link it to your dashboard.
                </p>
                <button onClick={() => openConnectModal()} className="px-6 py-2.5 bg-workspace-accent text-white rounded-lg transition-colors hover:bg-workspace-accent/90 inline-flex items-center font-medium">
                  <Plus className="w-5 h-5 mr-2" /> Connect WordPress Site
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredSites.map((site) => (
                  <SiteCard
                    key={site.id}
                    site={site}
                    loginLoading={loginLoading === site.id}
                    refreshingThumbnail={refreshingThumbnail === site.id}
                    activeDropdown={activeDropdown === site.id}
                    onToggleDropdown={() => setActiveDropdown(activeDropdown === site.id ? null : site.id)}
                    onMagicLogin={() => handleMagicLogin(site)}
                    onRefreshThumbnail={() => handleRefreshThumbnail(site)}
                    onDisconnect={() => { setActiveDropdown(null); handleDisconnectSite(site.id); }}
                    onDelete={() => { setActiveDropdown(null); handleDeleteSite(site.id); }}
                    onReconnect={() => openConnectModal(site)}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white dark:bg-card border border-border rounded-xl shadow-sm overflow-visible">
                <table className="w-full text-left text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-6 py-4 font-medium text-left">Site</th>
                      <th className="px-6 py-4 font-medium text-center">Updates</th>
                      <th className="px-6 py-4 font-medium text-center whitespace-nowrap">Uptime</th>
                      <th className="px-6 py-4 font-medium text-center">Errors</th>
                      <th className="px-6 py-4 font-medium text-center">Backups</th>
                      <th className="px-6 py-4 font-medium text-center whitespace-nowrap">WP/PHP Version</th>
                      <th className="px-6 py-4 font-medium text-center whitespace-nowrap">Magic Login</th>
                      <th className="pl-2 pr-6 py-4 font-medium w-16"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredSites.map((site) => (
                      <SiteListRow
                        key={site.id}
                        site={site}
                        loginLoading={loginLoading === site.id}
                        activeDropdown={activeDropdown === site.id}
                        onToggleDropdown={() => setActiveDropdown(activeDropdown === site.id ? null : site.id)}
                        onMagicLogin={(userId) => handleMagicLogin(site, userId)}
                        onDisconnect={() => { setActiveDropdown(null); handleDisconnectSite(site.id); }}
                        onDelete={() => { setActiveDropdown(null); handleDeleteSite(site.id); }}
                        onReconnect={() => openConnectModal(site)}
                        openUserDropdown={openUserDropdown === site.id}
                        onToggleUserDropdown={() => {
                          const next = openUserDropdown === site.id ? null : site.id;
                          setOpenUserDropdown(next);
                          if (next) fetchWpUsers(site);
                        }}
                        wpUsers={wpUsers[site.id] ?? []}
                        loadingUsers={loadingUsers === site.id}
                        onSetDefaultUser={(user) => { handleSetDefaultUser(site, user); setOpenUserDropdown(null); }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      case 'updates':
      case 'backups':
        return (
          <div className="flex flex-col items-center justify-center py-32 text-center h-full">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-muted/50 border border-border">
              {activeTab === 'updates' && <RefreshCw className="w-10 h-10 text-muted-foreground" />}
              {activeTab === 'backups' && <Database className="w-10 h-10 text-muted-foreground" />}
            </div>
            <h2 className="text-2xl font-bold mb-3 text-foreground capitalize">
              {activeTab === 'updates' ? 'All Updates' : activeTab === 'backups' ? 'All Backups' : activeTab}
            </h2>
            <p className="text-muted-foreground max-w-sm">This feature is coming soon to the AgencyPlus Dashboard.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0 relative">
      <Helmet>
        <title>Sites | AgencyPlus</title>
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={() => { }}
          isDarkMode={isGlobalDarkMode}
          showAdd={true}
          onAddClick={openConnectModal}
        />
      )}

      <main className="w-full p-0">
        <div className={`min-h-screen ${isGlobalDarkMode ? 'bg-background' : 'bg-white'} ${isMobile ? 'pt-14' : ''}`}>
          {renderTabContent()}
        </div>
      </main>

      <ConnectSiteModal
        show={showConnectModal}
        editingSite={editingSite}
        onClose={() => setShowConnectModal(false)}
        onSiteConnected={(newSite) => setConnectedSites(prev => [newSite, ...prev])}
        onSiteUpdated={(siteId, updates) => setConnectedSites(prev => prev.map(s => s.id === siteId ? { ...s, ...updates } : s))}
      />
    </div>
  );
}
